<template>
  <c-list-page
    :filter-groups="filterGroups"
    :pagination="pagination"
    :items="assets"
    :loading="downloadCenterStore.runningRequests.apiFetchDownloadCenterAssets"
    :header-config="pageHeaderConfig"
    :no-items-found-message="$t('l-download-center.no-items-found')"
    @update-filter="onFilterChange"
    @reset-filter="onResetFilter"
    @page-change="onPageChange"
  >
    <template #breadcrumb>
      <c-breadcrumb :class="b('breadcrumb')" />
    </template>
    <template #header>
      <c-page-header :config="pageHeaderConfig" />
    </template>
    <template #filters>
      <c-filters
        :filter-groups="filterGroups"
        @update-filter="onFilterChange"
      />
    </template>
    <template #items="{ items }">
      <c-teaser-tile-download-center
        v-for="(asset, index) in items"
        :key="index"
        :asset="asset"
      />
    </template>
  </c-list-page>
</template>

<script lang="ts">
  import { PropType, defineComponent } from 'vue';
  import useDownloadCenterStore from '@/stores/download-center';
  import cBreadcrumb from '@/components/c-breadcrumb.vue';
  import cFilters from '@/components/c-filters.vue';
  import cListPage from '@/components/c-list-page.vue';
  import cPageHeader, { PageHeaderConfig } from '@/components/c-page-header.vue';
  import cTeaserTileDownloadCenter from '@/components/c-teaser-tile-download-center.vue';
  import { getFilterGroupsWithSelection, resetAllFilters, updateFilterGroupsForFe } from '@/helpers/filters';
  import { generateFilterQuery } from '@/helpers/generate-filter-query';
  import { initializeFilterQueryAndPagination } from '@/helpers/initialize-filter-query-and-pagination';
  import { Pagination } from '@/helpers/pagination';
  import { DownloadCenterAsset } from '@/types/download-center-asset';
  import DownloadCenterConfig from '@/types/download-center-config';
  import { FilterGroup } from '@/types/filter';

  type Setup = {
    downloadCenterStore: ReturnType<typeof useDownloadCenterStore>;
  };

  type Data = {
    filterGroups: FilterGroup[];
    pagination: Pagination;
    filterQuery: string;
    assets: DownloadCenterAsset[];
  };

  /**
   * Renders Download Center:
   *  - List of downloadable assets
   *  - Filters
   */
  export default defineComponent({
    name: 'l-download-center',

    components: {
      cListPage,
      cBreadcrumb,
      cPageHeader,
      cFilters,
      cTeaserTileDownloadCenter,
    },

    props: {
      /**
       * The number of list items per page
       */
      numItemsPerPage: {
        type: Number,
        default: 24,
      },

      /**
       * The config object for the Download Center page
       */
      config: {
        type: Object as PropType<DownloadCenterConfig>,
        required: true,
      },
    },
    // emits: {},

    setup(): Setup {
      return {
        downloadCenterStore: useDownloadCenterStore(),
      };
    },

    data(): Data {
      return {
        filterQuery: '',
        pagination: new Pagination({ numItemsPerPage: this.numItemsPerPage }),
        filterGroups: [],
        assets: [],
      };
    },

    computed: {
      filterGroupsWithSelection(): FilterGroup[] {
        return getFilterGroupsWithSelection(this.filterGroups);
      },

      pageHeaderConfig(): PageHeaderConfig {
        return {
          heading: this.config.heading || this.$t('l-download-center.heading'),
          description: this.config.description,
          descriptionRight: this.config.descriptionRight,
          ctaPrimary: null,
          ctaSecondary: null,
        };
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      const url = new URL(window.location.href);

      const { filterQuery, pagination } = initializeFilterQueryAndPagination(url);

      this.filterQuery = filterQuery;

      if (pagination.current) {
        this.pagination.current = pagination.current;
      }

      this.updateList();
    },

    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      onFilterChange() {
        this.setUrlParams();
        this.updateList();
      },

      onResetFilter(): void {
        resetAllFilters(this.filterGroups);
        this.setUrlParams(true);
        this.updateList();
      },

      setUrlParams(clearAllFilters: boolean = false): void {
        const url = new URL(window.location.href);

        this.filterQuery = generateFilterQuery(url, this.filterGroupsWithSelection, this.pagination, clearAllFilters);

        window.history.replaceState({}, '', url);
      },

      onPageChange(page: number) {
        this.pagination.current = page;
        this.setUrlParams();
        this.updateList();
      },

      updateList(): void {
        this.downloadCenterStore.apiFetchDownloadCenterAssets(this.filterQuery, this.pagination).then((response) => {
          if (
            !response.data.length &&
            response.pagination.pageCount > 0 &&
            this.pagination.current > response.pagination.pageCount
          ) {
            this.onPageChange(1);

            return;
          }

          this.assets = response.data;
          this.filterGroups = updateFilterGroupsForFe(response.filterGroups);
          this.pagination.update(response.pagination);
        });
      },
    },
  });
</script>

<style lang="scss">
  @use 'sass:map';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/variables';

  .l-download-center {
    padding: 3rem 0;

    &__breadcrumb {
      margin-bottom: 3rem;
    }
  }
</style>
